import {
  CaretDoubleRightIcon,
  CaretLeftIcon,
  CaretRightIcon,
} from '@phosphor-icons/react';
import { Button as AriaButton } from 'react-aria-components';
import { cn } from './lib/cn';
import { describeRange, getPageItems } from './lib/pagination';
import { Select } from './Select';

export type PaginationProps = {
  /** 1-based. */
  page: number;
  pageSize: number;
  /** Rows across every page. */
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  pageSizeOptions?: number[];
  className?: string;
};

const DEFAULT_PAGE_SIZES = [10, 25, 50, 100];

/* Without Preflight a bare `<button>` keeps the user-agent styling. */
const RESET =
  'sk:m-0 sk:appearance-none sk:border-0 sk:bg-transparent sk:font-sans sk:cursor-pointer';

const CONTROL = [
  RESET,
  'sk:flex sk:h-7 sk:min-w-7 sk:items-center sk:justify-center sk:rounded-full',
  'sk:text-dense sk:font-normal sk:tabular-nums sk:text-fg-secondary sk:outline-none',
  'sk:transition-colors sk:hover:bg-fill-quaternary sk:hover:text-fg',
  'sk:disabled:cursor-default sk:disabled:opacity-30 sk:disabled:hover:bg-transparent',
].join(' ');
const ICON_CONTROL = `${CONTROL} sk:w-7 sk:px-0`;
const PAGE_CONTROL = `${CONTROL} sk:px-2`;

export function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZES,
  className,
}: PaginationProps) {
  const totalPages = Math.max(Math.ceil(total / Math.max(pageSize, 1)), 1);
  const items = getPageItems(page, totalPages);

  return (
    <nav
      aria-label="Pagination"
      className={cn(
        'sk:flex sk:flex-wrap sk:items-center sk:gap-x-4 sk:gap-y-2 sk:px-4 sk:py-3.5',
        'sk:shadow-[inset_0_1px_0_0_var(--sk-hairline)]',
        className,
      )}
    >
      <Select
        aria-label="Rows per page"
        options={pageSizeOptions.map(size => ({
          value: String(size),
          label: `${size} per page`,
        }))}
        value={String(pageSize)}
        onChange={value => onPageSizeChange(Number(value))}
        placement="top start"
        className="sk:h-7 sk:w-36 sk:tabular-nums"
        popoverClassName="sk:min-w-36"
      />

      <span className="sk:min-w-32 sk:flex-1 sk:whitespace-nowrap sk:text-center sk:text-dense sk:font-normal sk:text-fg-secondary sk:tabular-nums">
        Showing {describeRange(page, pageSize, total)}
      </span>

      <span className="sk:flex sk:items-center sk:gap-1.5">
        <AriaButton
          className={ICON_CONTROL}
          aria-label="Previous page"
          isDisabled={page <= 1}
          onPress={() => onPageChange(page - 1)}
        >
          <CaretLeftIcon size={12} />
        </AriaButton>

        {items.map(item =>
          typeof item === 'number' ? (
            <AriaButton
              key={item}
              className={cn(
                PAGE_CONTROL,
                'sk:inset-shadow-hairline sk:bg-surface',
                item === page &&
                  'sk:inset-shadow-none sk:bg-primary sk:text-surface sk:hover:bg-primary sk:hover:text-surface',
              )}
              aria-label={`Page ${item}`}
              aria-current={item === page ? 'page' : undefined}
              onPress={() => onPageChange(item)}
            >
              {item}
            </AriaButton>
          ) : (
            <span
              key={item}
              aria-hidden
              className="sk:flex sk:size-7 sk:items-center sk:justify-center sk:text-dense sk:font-normal sk:text-fg-tertiary"
            >
              …
            </span>
          ),
        )}

        <AriaButton
          className={ICON_CONTROL}
          aria-label="Next page"
          isDisabled={page >= totalPages}
          onPress={() => onPageChange(page + 1)}
        >
          <CaretRightIcon size={12} />
        </AriaButton>

        <AriaButton
          className={ICON_CONTROL}
          aria-label="Last page"
          isDisabled={page >= totalPages}
          onPress={() => onPageChange(totalPages)}
        >
          <CaretDoubleRightIcon size={12} />
        </AriaButton>
      </span>
    </nav>
  );
}

export type CursorPaginationProps = {
  /** 1-based: how many pages the reader has stepped through. */
  page: number;
  hasPrevious: boolean;
  hasNext: boolean;
  onPrevious: () => void;
  onNext: () => void;
  className?: string;
};

/**
 * Pages of a list read by cursor, whose total is unknown: the same frame and
 * controls as `Pagination`, with only the page reached and its neighbours.
 */
export function CursorPagination({
  page,
  hasPrevious,
  hasNext,
  onPrevious,
  onNext,
  className,
}: CursorPaginationProps) {
  return (
    <nav
      aria-label="Pagination"
      className={cn(
        'sk:flex sk:items-center sk:gap-x-4 sk:px-4 sk:py-3.5',
        'sk:shadow-[inset_0_1px_0_0_var(--sk-hairline)]',
        className,
      )}
    >
      <span className="sk:flex-1 sk:whitespace-nowrap sk:text-dense sk:font-normal sk:text-fg-secondary sk:tabular-nums">
        Page {page}
      </span>

      <span className="sk:flex sk:items-center sk:gap-1.5">
        <AriaButton
          className={ICON_CONTROL}
          aria-label="Previous page"
          isDisabled={!hasPrevious}
          onPress={onPrevious}
        >
          <CaretLeftIcon size={12} />
        </AriaButton>
        <AriaButton
          className={ICON_CONTROL}
          aria-label="Next page"
          isDisabled={!hasNext}
          onPress={onNext}
        >
          <CaretRightIcon size={12} />
        </AriaButton>
      </span>
    </nav>
  );
}
